<template>
  <NuxtLayout name="error">
    <div
      class="bg-surface-canvas dark:bg-surface-level-900 flex items-center justify-center min-h-screen"
    >
      <AppErrorState
        :icon="displayIcon"
        :title="displayTitle"
        :message="displayMessage"
        :error-code="statusCode"
        :primary-action="displayPrimaryAction"
        v-bind="displaySecondaryAction ? { secondaryAction: displaySecondaryAction } : {}"
      />
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { Lock, LogIn, SearchX, ServerCrash } from '@lucide/vue'
import { useI18n } from 'vue-i18n'
import AppErrorState from '~/components/shared/AppErrorState.vue'
import { Normalization } from '~/utils/normalization'

const props = defineProps<{
  /** Nuxt error payload rendered by the error boundary. */
  error: Error & { statusCode?: number; statusMessage?: string; message?: string; data?: unknown }
}>()

const { t } = useI18n()
const errorStore = useErrorStore()
const { startHttpErrorRecovery } = useHttpErrorRecovery()

// Nuxt's own unmatched-route 404 (vue-router `to.matched.length === 0`) is the only 404 that
// carries `error.data.path`; an app-thrown resource-not-found (handleHttpError.ts) never does.
function unmatchedRoutePath(error: { statusCode?: number; data?: unknown }): string | null {
  if (error.statusCode !== 404 || !Normalization.isRecord(error.data)) return null
  return Normalization.trimmedStringValue(error.data['path'])
}

// A stale-deploy chunk-load failure (NUXT_E1005) bypasses config.errorHandler and lands here
// directly, so this reports independently. watchEffect satisfies vue/no-setup-props-reactivity-loss.
watchEffect(() => {
  const path = unmatchedRoutePath(props.error)
  if (path !== null) {
    // Traffic/security signal, not an application failure -- kept out of the error level so
    // scanner noise (/v0, /careers, ...) never inflates the application error rate.
    useLogger('RouteNotFound').warn('unmatched', { path })
    return
  }
  useLogger('AppError').error('boundary', props.error)
})

const redirectAfterClear = (redirect: string) => {
  startHttpErrorRecovery()
  return clearError({ redirect })
}

// Configure default error configurations for each error type
const errorConfigs = computed(() => ({
  401: {
    icon: LogIn,
    message: t('common.errorPage.sessionExpiredMessage'),
    primaryAction: {
      label: 'Log in',
      onClick: () => {
        const { logout } = useIdentity()
        const userStore = useUserStore()
        void logout()
        userStore.clearUserState()
        return redirectAfterClear('/login')
      }
    },
    secondaryAction: {
      label: 'Go to Home',
      onClick: () => {
        return redirectAfterClear('/')
      }
    },
    title: 'Session expired'
  },
  403: {
    icon: Lock,
    message: t('common.errorPage.accessDeniedMessage'),
    primaryAction: {
      label: 'Go to Dashboard',
      onClick: () => {
        return redirectAfterClear('/dashboard')
      }
    },
    secondaryAction: {
      label: 'Go back',
      onClick: () => {
        startHttpErrorRecovery()
        return clearError().then(() => {
          window.history.back()
        })
      }
    },
    title: 'Access denied'
  },
  404: {
    icon: SearchX,
    message: t('common.errorPage.pageNotFoundMessage'),
    primaryAction: {
      label: 'Go to Home',
      onClick: () => {
        return redirectAfterClear('/')
      }
    },
    title: 'Page not found'
  },
  default: {
    icon: ServerCrash,
    message: t('common.errorPage.unexpectedErrorMessage'),
    primaryAction: {
      label: 'Reload page',
      onClick: () => {
        window.location.reload()
      }
    },
    secondaryAction: {
      label: 'Go to Home',
      onClick: () => {
        return redirectAfterClear('/')
      }
    },
    title: 'Something went wrong'
  }
}))

// Determine which configuration to use
const statusCode = computed(() => {
  const rawStatusCode: unknown = props.error?.statusCode
  return typeof rawStatusCode === 'number' ? rawStatusCode : 500
})
const config = computed(
  () =>
    errorConfigs.value[statusCode.value as keyof typeof errorConfigs.value] ??
    errorConfigs.value.default
)

// Prioritize errorStore over default config
const displayIcon = computed(() =>
  errorStore.hasError && errorStore.icon ? errorStore.icon : config.value.icon
)
const displayTitle = computed(() =>
  errorStore.hasError && errorStore.title ? errorStore.title : config.value.title
)
const displayMessage = computed(() =>
  errorStore.hasError && errorStore.message() ? errorStore.message() : config.value.message
)
const displayPrimaryAction = computed(() =>
  errorStore.hasError && errorStore.primaryAction
    ? errorStore.primaryAction
    : config.value.primaryAction
)
const displaySecondaryAction = computed(() => {
  if (errorStore.hasError && errorStore.secondaryAction) return errorStore.secondaryAction
  if ('secondaryAction' in config.value) return config.value.secondaryAction
  return undefined
})

// Clean up the store when the component is unmounted
onUnmounted(() => {
  errorStore.clearError()
})
</script>
